import { Capacitor } from '@capacitor/core';

/**
 * Platform detection, used to branch behaviour that must differ between the
 * web build and the two store builds.
 *
 * Everything here is safe to call in a plain browser: `@capacitor/core` reports
 * platform `web` and `isNativePlatform() === false` when no native bridge is
 * present, so no caller needs to guard.
 */

/** 'ios' | 'android' | 'web' */
export const platform = Capacitor.getPlatform();

export const isNative = Capacitor.isNativePlatform();
export const isIOS = platform === 'ios';
export const isAndroid = platform === 'android';
export const isWeb = !isNative;

/** True when running as an installed PWA rather than a browser tab. */
export const isStandalonePWA =
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator.standalone === true);

/**
 * True when the app is presented as an app rather than a web page — either a
 * native shell or an installed PWA. Used to hide web-only affordances such as
 * "install this app" prompts and external marketing links.
 */
export const isAppLike = isNative || isStandalonePWA;

/**
 * Stamps the platform onto the root element so CSS can target native builds
 * (`html.is-native`, `html.platform-ios`) without another JS round trip.
 */
export function applyPlatformClasses() {
  const root = document.documentElement;
  root.classList.add(`platform-${platform}`);
  if (isNative) root.classList.add('is-native');
  if (isStandalonePWA) root.classList.add('is-standalone');
  if (isAppLike) root.classList.add('is-app-like');
}
